import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { HeartIcon, TargetIcon, QuoteIcon, ShieldCheckIcon, UsersIcon, StarIcon, CompassIcon, AwardIcon, LinkedinIcon } from 'lucide-react';
interface AboutPageProps {
  onNavigate: (page: string) => void;
}
function AboutHero({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <section className="relative w-full min-h-[50vh] flex items-end overflow-hidden" data-id="element-456">
      <div className="absolute inset-0" data-id="element-457">
        <img src="https://images.unsplash.com/photo-1605649487212-47bdab064df7?w=1920&q=80" alt="Nepal Luxury Travel team in the Himalayas" className="w-full h-full object-cover" data-id="element-458" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" data-id="element-459" />
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 pt-32" data-id="element-460">
        <nav className="mb-5" aria-label="Breadcrumb" data-id="element-461">
          <ol className="flex items-center gap-2 text-sm text-white/60" data-id="element-462">
            <li data-id="element-463">
              <button onClick={() => onNavigate('home')} className="hover:text-white transition-colors" data-id="element-464">
                Home
              </button>
            </li>
            <li data-id="element-465">/</li>
            <li className="text-[#D4A853]" data-id="element-466">About Us</li>
          </ol>
        </nav>
        <motion.h1 initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" data-id="element-467">
          About{' '}
          <span className="italic font-medium text-[#D4A853]" data-id="element-468">
            Nepal Luxury Travel
          </span>
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.15
      }} className="text-white/70 text-lg max-w-2xl font-light" data-id="element-469">
          Crafting extraordinary Himalayan journeys since 2010
        </motion.p>
      </div>
    </section>;
}
function OurStory() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-[#FAF8F5] py-20 sm:py-28" ref={ref} data-id="element-470">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-471">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-472">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7
        }} data-id="element-473">
            <div className="flex items-center gap-3 mb-6" data-id="element-474">
              <span className="text-[#D4A853]/40 text-xs" data-id="element-475">◆</span>
              <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-476" />
              <span className="text-[#D4A853]/40 text-xs" data-id="element-477">◆</span>
            </div>
            <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-6" data-id="element-478">
              Our Story
            </h2>
            <div className="space-y-4 text-slate-600 leading-relaxed text-base" data-id="element-479">
              <p data-id="element-480">
                Nepal Luxury Travel was born from a simple belief: that the
                world's most extraordinary landscapes deserve to be experienced
                with the highest level of comfort and care. Founded in 2010 by a
                team of passionate Nepali mountaineers and hospitality
                professionals, we set out to redefine adventure travel in the
                Himalayas.
              </p>
              <p data-id="element-481">
                What started as a small operation running premium treks to
                Everest Base Camp has grown into Nepal's most respected luxury
                travel company. Today, we offer over 50 curated experiences
                across the country — from helicopter tours over the world's
                highest peaks to intimate cultural journeys through ancient
                Newari cities.
              </p>
              <p data-id="element-482">
                Our success is built on relationships — with the communities we
                visit, the guides who lead our journeys, and the travelers who
                trust us with their once-in-a-lifetime adventures. Every journey
                we craft is a reflection of our deep love for Nepal and our
                commitment to sharing it responsibly.
              </p>
            </div>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="rounded-2xl overflow-hidden" data-id="element-483">
            <img src="https://images.unsplash.com/photo-1558799401-1dcba79834c2?w=800&q=80" alt="Nepal Luxury Travel founders in Kathmandu" className="w-full aspect-[4/3] object-cover" data-id="element-484" />
          </motion.div>
        </div>
      </div>
    </section>;
}
function MissionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-white py-20 sm:py-28" ref={ref} data-id="element-485">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-486">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-487">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="bg-[#FAF8F5] rounded-2xl p-8 sm:p-10 border border-[#D4A853]/10" data-id="element-488">
            <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center mb-6" data-id="element-489">
              <HeartIcon className="w-7 h-7 text-teal-600" data-id="element-490" />
            </div>
            <h3 className="font-playfair text-2xl font-bold text-slate-900 mb-4" data-id="element-491">
              Our Mission
            </h3>
            <p className="text-slate-600 leading-relaxed text-base" data-id="element-492">
              To create transformative travel experiences that connect
              discerning travelers with Nepal's natural beauty, rich culture,
              and warm hospitality — while supporting local communities and
              preserving the environment for future generations.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="bg-[#FAF8F5] rounded-2xl p-8 sm:p-10 border border-[#D4A853]/10" data-id="element-493">
            <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center mb-6" data-id="element-494">
              <TargetIcon className="w-7 h-7 text-teal-600" data-id="element-495" />
            </div>
            <h3 className="font-playfair text-2xl font-bold text-slate-900 mb-4" data-id="element-496">
              Our Vision
            </h3>
            <p className="text-slate-600 leading-relaxed text-base" data-id="element-497">
              To be the world's most trusted luxury travel company in the
              Himalayas — known for exceptional service, authentic experiences,
              and a genuine commitment to sustainable tourism that benefits
              Nepal and its people.
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}
function FoundersWords() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-[#0C1F2C] py-20 sm:py-28" ref={ref} data-id="element-498">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-499">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} data-id="element-500">
          <QuoteIcon className="w-10 h-10 text-[#D4A853]/30 mx-auto mb-6" data-id="element-501" />
          <blockquote className="font-playfair text-2xl sm:text-3xl font-medium text-white leading-relaxed mb-8 italic" data-id="element-502">
            "Nepal is not just a destination — it is a feeling. The mountains
            teach you humility, the people teach you warmth, and the journey
            teaches you who you truly are. Our job is simply to make that
            journey as beautiful as possible."
          </blockquote>
          <div className="flex items-center justify-center gap-4" data-id="element-503">
            <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80" alt="Rajesh Shrestha, Founder" className="w-14 h-14 rounded-full object-cover border-2 border-[#D4A853]/30" data-id="element-504" />
            <div className="text-left" data-id="element-505">
              <p className="text-white font-semibold" data-id="element-506">Rajesh Shrestha</p>
              <p className="text-slate-400 text-sm" data-id="element-507">
                Founder & Managing Director
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}
function TeamSection({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-[#FAF8F5] py-20 sm:py-28" ref={ref} data-id="element-508">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-509">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="text-center mb-14" data-id="element-510">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-511">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-512">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-513" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-514">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-id="element-515">
            Our Team
          </h2>
          <p className="text-slate-500 font-light text-lg" data-id="element-516">
            The passionate people behind your journey
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-517">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0
        }} className="text-center cursor-pointer group" onClick={() => onNavigate('guide-rajesh')} data-id="element-518">
            <div className="w-36 h-36 rounded-full overflow-hidden mx-auto mb-4 border-3 border-[#D4A853]/20 group-hover:border-[#D4A853]/60 transition-colors duration-300 group-hover:shadow-lg group-hover:shadow-[#D4A853]/10" data-id="element-519">
              <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&q=80" alt="Rajesh Shrestha" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-520" />
            </div>
            <h3 className="font-playfair text-lg font-semibold text-slate-900 group-hover:text-teal-700 transition-colors" data-id="element-521">
              Rajesh Shrestha
            </h3>
            <p className="text-teal-600 text-sm font-medium mb-2" data-id="element-522">
              Founder & MD
            </p>
            <p className="text-slate-500 text-sm" data-id="element-523">
              20+ years in luxury travel. Former mountaineer with 3 Everest
              summits.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-center cursor-pointer group" onClick={() => onNavigate('guide-sita')} data-id="element-524">
            <div className="w-36 h-36 rounded-full overflow-hidden mx-auto mb-4 border-3 border-[#D4A853]/20 group-hover:border-[#D4A853]/60 transition-colors duration-300 group-hover:shadow-lg group-hover:shadow-[#D4A853]/10" data-id="element-525">
              <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&q=80" alt="Sita Gurung" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-526" />
            </div>
            <h3 className="font-playfair text-lg font-semibold text-slate-900 group-hover:text-teal-700 transition-colors" data-id="element-527">
              Sita Gurung
            </h3>
            <p className="text-teal-600 text-sm font-medium mb-2" data-id="element-528">
              Head of Operations
            </p>
            <p className="text-slate-500 text-sm" data-id="element-529">
              Expert in logistics and guest experience. Fluent in 4 languages.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-center cursor-pointer group" onClick={() => onNavigate('guide-pemba')} data-id="element-530">
            <div className="w-36 h-36 rounded-full overflow-hidden mx-auto mb-4 border-3 border-[#D4A853]/20 group-hover:border-[#D4A853]/60 transition-colors duration-300 group-hover:shadow-lg group-hover:shadow-[#D4A853]/10" data-id="element-531">
              <img src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&q=80" alt="Pemba Sherpa" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-532" />
            </div>
            <h3 className="font-playfair text-lg font-semibold text-slate-900 group-hover:text-teal-700 transition-colors" data-id="element-533">
              Pemba Sherpa
            </h3>
            <p className="text-teal-600 text-sm font-medium mb-2" data-id="element-534">
              Lead Trek Guide
            </p>
            <p className="text-slate-500 text-sm" data-id="element-535">
              15 years guiding experience. Certified mountaineer and first-aid
              expert.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="text-center cursor-pointer group" onClick={() => onNavigate('guide-maya')} data-id="element-536">
            <div className="w-36 h-36 rounded-full overflow-hidden mx-auto mb-4 border-3 border-[#D4A853]/20 group-hover:border-[#D4A853]/60 transition-colors duration-300 group-hover:shadow-lg group-hover:shadow-[#D4A853]/10" data-id="element-537">
              <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&q=80" alt="Maya Tamang" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-538" />
            </div>
            <h3 className="font-playfair text-lg font-semibold text-slate-900 group-hover:text-teal-700 transition-colors" data-id="element-539">
              Maya Tamang
            </h3>
            <p className="text-teal-600 text-sm font-medium mb-2" data-id="element-540">
              Guest Relations
            </p>
            <p className="text-slate-500 text-sm" data-id="element-541">
              Hospitality specialist ensuring every guest feels welcomed and
              cared for.
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}
function WhyChooseUsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-white py-20 sm:py-28" ref={ref} data-id="element-542">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-543">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="text-center mb-14" data-id="element-544">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-545">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-546">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-547" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-548">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-id="element-549">
            Why Choose Us
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-550">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0
        }} className="text-center bg-[#FAF8F5] rounded-2xl p-8 border border-[#D4A853]/10" data-id="element-551">
            <div className="w-14 h-14 bg-[#D4A853]/10 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-552">
              <ShieldCheckIcon className="w-7 h-7 text-teal-600" data-id="element-553" />
            </div>
            <h3 className="font-playfair text-lg font-semibold text-slate-900 mb-2" data-id="element-554">
              Safety First
            </h3>
            <p className="text-slate-500 text-base" data-id="element-555">
              Comprehensive safety protocols, satellite phones, and emergency
              evacuation plans.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.08
        }} className="text-center bg-[#FAF8F5] rounded-2xl p-8 border border-[#D4A853]/10" data-id="element-556">
            <div className="w-14 h-14 bg-[#D4A853]/10 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-557">
              <UsersIcon className="w-7 h-7 text-teal-600" data-id="element-558" />
            </div>
            <h3 className="font-playfair text-lg font-semibold text-slate-900 mb-2" data-id="element-559">
              Expert Guides
            </h3>
            <p className="text-slate-500 text-base" data-id="element-560">
              Certified, multilingual guides with decades of local experience
              and knowledge.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.16
        }} className="text-center bg-[#FAF8F5] rounded-2xl p-8 border border-[#D4A853]/10" data-id="element-561">
            <div className="w-14 h-14 bg-[#D4A853]/10 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-562">
              <StarIcon className="w-7 h-7 text-teal-600" data-id="element-563" />
            </div>
            <h3 className="font-playfair text-lg font-semibold text-slate-900 mb-2" data-id="element-564">
              Luxury Stays
            </h3>
            <p className="text-slate-500 text-base" data-id="element-565">
              Hand-selected five-star hotels and premium lodges throughout your
              journey.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.24
        }} className="text-center bg-[#FAF8F5] rounded-2xl p-8 border border-[#D4A853]/10" data-id="element-566">
            <div className="w-14 h-14 bg-[#D4A853]/10 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-567">
              <CompassIcon className="w-7 h-7 text-teal-600" data-id="element-568" />
            </div>
            <h3 className="font-playfair text-lg font-semibold text-slate-900 mb-2" data-id="element-569">
              Tailored Trips
            </h3>
            <p className="text-slate-500 text-base" data-id="element-570">
              Every itinerary is fully customizable to your preferences and
              schedule.
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}
export function AboutPage({
  onNavigate
}: AboutPageProps) {
  return <div className="w-full" data-id="element-571">
      <AboutHero onNavigate={onNavigate} data-id="element-572" />
      <OurStory data-id="element-573" />
      <MissionSection data-id="element-574" />
      <FoundersWords data-id="element-575" />
      <TeamSection onNavigate={onNavigate} data-id="element-576" />
      <WhyChooseUsSection data-id="element-577" />
    </div>;
}